/* @jsx h */
import { defineComponent, h, ref, computed, watch, PropType } from "vue";
import { useQuery, useInfiniteQuery } from "@tanstack/vue-query";
import { formatPriceInLocale } from "@lemmonhq/orders";
import {
  ResponsiveTable,
  type ResponsiveTableColumn,
  useLocalization,
} from "@lemmonhq/uikit";
import { useAuth, useTrpc } from "@app/vue/providers/index";
import { EntriesModal } from "../components/EntriesModal";

type ProductRevenue = {
  product_id: number;
  product_name: string;
  category_id: number;
  category_name: string;
  gross_revenue: number | null;
  discounts: number | null;
  promotions: number | null;
  net_revenue: number | null;
  net_vat: number | null;
  items: number;
};

type SortKey = "net_revenue" | "gross_revenue" | "discounts" | "items";

const SORTS: SortKey[] = ["net_revenue", "gross_revenue", "discounts", "items"];

export const ProductsTab = defineComponent({
  name: "ChartProductsTab",
  props: {
    from: { type: String, required: true },
    to: { type: String, required: true },
    categoryId: { type: Number as PropType<number | null>, default: null },
    onCategoryChange: {
      type: Function as PropType<(categoryId: number | null) => void>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const auth = useAuth();
    const currency = () => auth.user.value?.restaurant.currency || "CHF";

    const search = ref("");
    const debouncedSearch = ref("");
    let searchTimer: ReturnType<typeof setTimeout> | undefined;
    watch(search, (v) => {
      clearTimeout(searchTimer);
      searchTimer = setTimeout(() => (debouncedSearch.value = v), 300);
    });

    const sort = ref<SortKey>("net_revenue");

    // Category options come from the (cached) category-revenue query.
    const categoryQuery = useQuery({
      queryKey: computed(() => [
        "accounting",
        "rev-category",
        props.from,
        props.to,
      ]),
      queryFn: () =>
        trpc.accounting.revenueByCategory.query({
          from: props.from,
          to: props.to,
        }),
    });
    const categoryOptions = computed(() =>
      (categoryQuery.data.value ?? []).map((c) => ({
        id: c.category_id,
        name: c.category_name,
      })),
    );

    const productsQuery = useInfiniteQuery({
      queryKey: computed(() => [
        "accounting",
        "rev-product",
        props.from,
        props.to,
        props.categoryId,
        debouncedSearch.value,
        sort.value,
      ]),
      queryFn: ({ pageParam }) =>
        trpc.accounting.revenueByProduct.query({
          from: props.from,
          to: props.to,
          category_id: props.categoryId ?? undefined,
          search: debouncedSearch.value || undefined,
          sort: sort.value,
          limit: 25,
          offset: (pageParam as number) ?? 0,
        }),
      initialPageParam: 0,
      getNextPageParam: (lastPage: any, allPages: any[]) =>
        lastPage.has_more ? allPages.length * 25 : undefined,
    });

    const products = computed<ProductRevenue[]>(
      () =>
        productsQuery.data.value?.pages.flatMap(
          (p: any) => p.data as ProductRevenue[],
        ) ?? [],
    );
    const totalCount = computed<number>(
      () => productsQuery.data.value?.pages?.[0]?.total_count ?? 0,
    );

    const onScroll = (e: Event) => {
      const el = e.target as HTMLElement;
      if (
        el.scrollHeight - el.scrollTop - el.clientHeight < 300 &&
        productsQuery.hasNextPage.value &&
        !productsQuery.isFetchingNextPage.value
      ) {
        productsQuery.fetchNextPage();
      }
    };

    const activeProduct = ref<ProductRevenue | null>(null);

    const columns = computed<ResponsiveTableColumn[]>(() => [
      {
        key: "product",
        label: t("reports.coa.col_product"),
        render: (row: ProductRevenue) => (
          <div class="flex flex-col min-w-0">
            <span class="font-medium text-base-content truncate">
              {row.product_name}
            </span>
            <span class="text-[11px] text-base-content/45 truncate">
              {row.category_name}
            </span>
          </div>
        ),
        mobilePriority: "primary",
      },
      {
        key: "items",
        label: t("reports.coa.col_items"),
        headerClass: "text-right",
        cellClass: "text-right tabular-nums text-base-content/70",
        render: (row: ProductRevenue) => row.items.toLocaleString(),
        mobilePriority: "secondary",
      },
      {
        key: "gross_revenue",
        label: t("reports.coa.rev_gross_revenue"),
        headerClass: "text-right",
        cellClass: "text-right tabular-nums text-base-content/70",
        render: (row: ProductRevenue) =>
          formatPriceInLocale(row.gross_revenue ?? 0, false, 2, currency()),
        mobilePriority: "secondary",
      },
      {
        key: "discounts",
        label: t("reports.coa.rev_discounts"),
        headerClass: "text-right",
        cellClass: "text-right tabular-nums",
        render: (row: ProductRevenue) =>
          row.discounts ? (
            <span class="text-warning">
              {formatPriceInLocale(row.discounts, false, 2, currency())}
            </span>
          ) : (
            <span class="text-base-content/25">—</span>
          ),
        mobilePriority: "secondary",
      },
      {
        key: "net_revenue",
        label: t("reports.coa.rev_net_revenue"),
        headerClass: "text-right",
        cellClass: "text-right tabular-nums",
        render: (row: ProductRevenue) => (
          <span class="font-semibold text-base-content">
            {formatPriceInLocale(row.net_revenue ?? 0, true, 2, currency())}
          </span>
        ),
        mobilePriority: "primary",
        mobilePrimaryClass: "text-right shrink-0",
      },
    ]);

    return () => (
      <div class="flex flex-col gap-4 h-full min-h-0">
        {/* Controls */}
        <div class="flex flex-col sm:flex-row gap-2 sm:items-center">
          <div class="relative flex-1">
            <span class="absolute inset-y-0 left-0 flex items-center pl-3">
              <span class="material-symbols-outlined text-base-content/40 text-base">
                search
              </span>
            </span>
            <input
              type="text"
              class="input input-bordered input-sm w-full pl-9"
              placeholder={t("reports.coa.search_products")}
              value={search.value}
              onInput={(e: any) => (search.value = e.target.value)}
            />
          </div>
          <select
            class="select select-bordered select-sm"
            value={props.categoryId == null ? "" : String(props.categoryId)}
            onChange={(e: any) =>
              props.onCategoryChange(
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
          >
            <option value="">{t("reports.coa.all_categories")}</option>
            {categoryOptions.value.map((c) => (
              <option value={String(c.id)} key={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            class="select select-bordered select-sm"
            value={sort.value}
            onChange={(e: any) => (sort.value = e.target.value)}
          >
            {SORTS.map((s) => (
              <option value={s} key={s}>
                {t(`reports.coa.sort_${s}`)}
              </option>
            ))}
          </select>
        </div>

        <div class="text-xs text-base-content/50">
          {t("reports.coa.product_count", [totalCount.value])}
        </div>

        {/* Product list */}
        <div class="flex-1 min-h-0 overflow-y-auto" onScroll={onScroll}>
          {productsQuery.isError.value ? (
            <div class="flex flex-col items-center justify-center py-16 text-error gap-2">
              <span class="material-symbols-outlined text-4xl opacity-60">
                error_outline
              </span>
              <p class="text-sm">{t("reports.coa.error_load")}</p>
              <button
                class="btn btn-sm btn-outline btn-error"
                onClick={() => productsQuery.refetch()}
              >
                {t("reports.coa.retry")}
              </button>
            </div>
          ) : (
            <ResponsiveTable
              columns={columns.value}
              rows={products.value}
              rowKey={(row: ProductRevenue) => row.product_id}
              loading={productsQuery.isLoading.value}
              loadingMore={productsQuery.isFetchingNextPage.value}
              emptyMessage={t("reports.coa.empty_revenue")}
              onRowClick={(row: ProductRevenue) => (activeProduct.value = row)}
            />
          )}
        </div>

        {activeProduct.value && (
          <EntriesModal
            title={activeProduct.value.product_name}
            category={activeProduct.value.category_name}
            productId={activeProduct.value.product_id}
            showAccount={true}
            from={props.from}
            to={props.to}
            onClose={() => (activeProduct.value = null)}
          />
        )}
      </div>
    );
  },
});
